import React, { useCallback, useEffect, useRef, useState } from 'react';
import { requestTrialUpgrade } from './components/TrialUpgrade';
import { SearchLoader, useSearchLoaderPhase } from './components/SearchLoader';
import { useAuth } from './AuthContext';
import {
  SearchFilters, EMPTY_FILTERS,
  ChipInput, FilterSection, RangeInput, ScopeSelect, TitleSyntaxTip, useUniverseIndustries,
  useUniverseLanguages, LanguageCertSections,
  EMPLOYMENT_TYPE_GROUPS, expandEmploymentGroups, GlobalSearchNotice, hasPeopleLocation, LocationInput, NearInput, nearPayload, SuggestChipInput,
  notPayload, signalsApply, EmploymentSignalsToggle, KeywordGroupsEditor, withKeywordGroups, keywordGroupsPayload, SuggestRelatedTitles, pasteListIntoPrompt, BuildGaps } from './V2Lab';
import { tourEvent, useLessonAction } from './tour/tourEvents';
import { useTour } from './tour/TourProvider';
import { SearchTemplatesBar } from './SearchTemplates';
import { releaseLeaveGuard } from './navGuard';

/**
 * HM Search — the hiring-manager surface over the Forager universe.
 *
 * "Find Contacts" on a project's Companies tab deep-links here: it
 * drops the selected companies into sessionStorage, this page seeds them as
 * the fixed company frame and runs. Searches are built as PERSONAS — each
 * one a complete filter set of its own ("Engineering Director + data
 * science", "any Talent Acquisition") — OR-combined server-side
 * (filters.orGroups), with per-persona counts and match badges, so one
 * persona's keywords never constrain another's titles. The FULL search
 * (candidates + companies, Sample | Universe) lives at /search.
 */

const PAGE_SIZE = 50;
const FHM_SEED_KEY = 'universe_search_seed';
const MAX_GROUPS = 6;

// Badge palette per persona (index-stable).
const GROUP_COLORS = [
  { bg: 'bg-violet-100', text: 'text-violet-700', solid: 'bg-violet-500' },
  { bg: 'bg-teal-100', text: 'text-teal-700', solid: 'bg-teal-500' },
  { bg: 'bg-amber-100', text: 'text-amber-700', solid: 'bg-amber-500' },
  { bg: 'bg-rose-100', text: 'text-rose-700', solid: 'bg-rose-500' },
  { bg: 'bg-sky-100', text: 'text-sky-700', solid: 'bg-sky-500' },
  { bg: 'bg-lime-100', text: 'text-lime-700', solid: 'bg-lime-600' },
];

// One-click starting point for hiring-manager hunts — chips the user can
// prune, not a hidden filter.
const HIRING_MANAGER_TITLES = [
  'head of', 'director', 'vp ', 'chief', 'hiring manager', 'talent acquisition', 'recruitment manager', 'people operations',
];

type PersonRow = {
  id: number; name: string; title: string | null; company: string | null;
  location: string | null; linkedin_url: string | null; headline: string | null;
  skills: string | null; current_start: string | null; experience_years: number | null;
  matched_groups?: number[];
};

type Persona = { name: string; f: SearchFilters };

// Learn tab — "Find hiring managers": the example personas "Set them up for
// me" creates (keep in step with the lesson text in tour/tours.ts).
const LESSON_PERSONAS: Persona[] = [
  { name: 'C-Level', f: { ...EMPTY_FILTERS, titles: ['chief', 'ceo', 'cto', 'founder', 'managing director'] } },
  { name: 'Talent Acquisition', f: { ...EMPTY_FILTERS, titles: ['talent acquisition', 'recruiter', 'recruitment', 'head of people', 'people partner'] } },
  { name: 'AI Engineer Direct Hiring Managers', f: { ...EMPTY_FILTERS, titles: ['head of ai', 'head of machine learning', 'ai lead', 'engineering manager', 'head of data', 'director of engineering', 'vp engineering'] } },
];
const LESSON_LOCATION = 'Denmark';
// The title the lesson has people take out ("too broad") before searching.
const LESSON_REMOVE_TITLE = 'engineering manager';
// Where the lesson's filters end up — searched in the background as soon as
// the lesson reaches this page, so pressing Search is instant.
const LESSON_FINAL: Persona[] = LESSON_PERSONAS.map((p) => ({
  ...p,
  f: { ...p.f, titles: p.f.titles.filter((t) => t !== LESSON_REMOVE_TITLE), locations: [LESSON_LOCATION] },
}));

const personaCount = (f: SearchFilters) =>
  (f.nearPlace && f.nearPlace.trim() ? 1 : 0) +
  (f.titlesExclude || []).length + (f.skillsExclude || []).length + (f.headlineKeywordsExclude || []).length +
  f.titles.length + f.locations.length + f.anywhereKeywords.length + f.headlineKeywords.length +
  f.summaryKeywords.length + f.skills.length + f.roleKeywords.length +
  f.industries.length + f.employmentTypes.length + f.schools.length + f.degrees.length +
  (f.tenureMin ? 1 : 0) + (f.tenureMax ? 1 : 0) + (f.joinedMin ? 1 : 0) + (f.joinedMax ? 1 : 0) + (f.experienceMin ? 1 : 0) + (f.experienceMax ? 1 : 0) +
  (f.languages || []).length + (f.certifications || []).length +
  (f.keywordGroups || []).reduce((n, g) => n + g.terms.length + g.exclude.length, 0);

const ContactSearch: React.FC = () => {
  const { token } = useAuth();
  const authHeaders = useCallback((): Record<string, string> => ({ Authorization: `Bearer ${token}` }), [token]);
  const industryOpts = useUniverseIndustries(true, authHeaders);
  const languageNames = useUniverseLanguages(true, authHeaders);

  const [status, setStatus] = useState<null | { people: number; companies: number; error?: string; importLimit?: number | null; importLimitPlan?: string }>(null);
  const [seedEntries, setSeedEntries] = useState<string[]>([]);
  const [seedLabel, setSeedLabel] = useState<string | null>(null);
  // What the user said they're hunting (Find Contacts modal in the project).
  const [intent, setIntent] = useState<'candidates' | 'hiring-managers'>('hiring-managers');
  // Review mode (clicked a company's 0 hiring-manager associations):
  // eyeball employees, select, add straight into the project as HMs.
  const [review, setReview] = useState<null | { projectId: number; companyId?: number; companyName?: string; projectName?: string }>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // "Select all N matches" — server re-runs the search and adds every
  // match, not just the page's explicit ids.
  const [allMatches, setAllMatches] = useState(false);
  // Profile drawer: full universe record for a clicked person.
  const [profile, setProfile] = useState<any | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const openProfile = async (personId: number) => {
    setProfileLoading(true);
    setProfile({ id: personId });
    try {
      const resp = await fetch(`/api/universe/person/${personId}`, { headers: authHeaders() });
      if (resp.ok) setProfile((await resp.json()).person);
    } catch { /* drawer shows what it has */ }
    setProfileLoading(false);
  };
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState<string | null>(null);
  // Adding hiring managers runs as a background job (any number — no cap);
  // progress shows in a modal and the project opens when it's done.
  const [hmJob, setHmJob] = useState<any | null>(null);
  // Import the whole result set into a NEW project (persona names become
  // candidate tags). Inline progress, then opens the project.
  const [importName, setImportName] = useState('');
  const [importJob, setImportJob] = useState<any | null>(null);
  // NL builder: a sentence becomes people filters (companies stay fixed by
  // the seed). Variants land as personas.
  const [buildPrompt, setBuildPrompt] = useState('');
  const [building, setBuilding] = useState(false);
  const [buildNote, setBuildNote] = useState<string | null>(null);
  const [buildGaps, setBuildGaps] = useState<string[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([{ name: 'Persona 1', f: EMPTY_FILTERS }]);
  const [active, setActive] = useState(0);
  // Personas are OPT-IN: most hunts are one search, and nobody should get
  // tagged "Persona 1" by a default they never chose. The button enables
  // the OR-combined mode; until then it's a plain single search.
  const [personasEnabled, setPersonasEnabled] = useState(false);
  const [view, setView] = useState<'list' | 'grouped'>('list');
  const [results, setResults] = useState<PersonRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [groupCounts, setGroupCounts] = useState<number[] | null>(null);
  const [groupRows, setGroupRows] = useState<any[] | null>(null);
  const [page, setPage] = useState(0);
  const [searching, setSearching] = useState(false);
  // The animated loader: only after 400ms of searching, fades out on results.
  const loaderPhase = useSearchLoaderPhase(searching && page === 0);
  const [error, setError] = useState<string | null>(null);

  const stateRef = useRef({ personas, seedEntries, personasEnabled });
  stateRef.current = { personas, seedEntries, personasEnabled };

  const cur = personas[active] || personas[0];
  const setF = (patch: Partial<SearchFilters>) =>
    setPersonas(personas.map((p, i) => (i === active ? { ...p, f: { ...p.f, ...patch } } : p)));

  // ---- Learn tab lesson ("Find hiring managers") ---------------------------
  const { stepId: lessonStep, running: runningLesson } = useTour();
  const inLesson = runningLesson === 'find-hiring-managers';
  useLessonAction('cs-setup-personas', () => {
    setPersonas(LESSON_PERSONAS.map((p) => ({ ...p, f: { ...p.f } })));
    setActive(0);
    setPersonasEnabled(true);
  });
  useLessonAction('cs-remove-title', () => {
    const i = personas.findIndex((p) => p.f.titles.includes(LESSON_REMOVE_TITLE));
    if (i < 0) return;
    setActive(i);
    setPersonas((ps) => ps.map((p) => ({ ...p, f: { ...p.f, titles: p.f.titles.filter((t) => t !== LESSON_REMOVE_TITLE) } })));
  });
  useLessonAction('cs-add-location', () => {
    setPersonas((ps) => ps.map((p) => (personaCount(p.f) > 0 && !p.f.locations.includes(LESSON_LOCATION)
      ? { ...p, f: { ...p.f, locations: [...p.f.locations, LESSON_LOCATION] } } : p)));
  });
  useLessonAction('cs-select-all', () => {
    setSelectedIds(new Set(results.map((r) => r.id)));
    if ((total || 0) > results.length) setAllMatches(true);
  });
  // Move on however the user did it — by hand or with the lesson's button.
  useEffect(() => {
    if (!lessonStep) return;
    const used = personas.filter((p) => personaCount(p.f) > 0);
    if (lessonStep === 'hm-personas' && personasEnabled && used.length >= 2) tourEvent('cs-personas-ready');
    if (lessonStep === 'hm-review-titles' && used.length > 0 && !used.some((p) => p.f.titles.includes(LESSON_REMOVE_TITLE))) tourEvent('cs-title-removed');
    if (lessonStep === 'hm-location' && used.length > 0 && used.every((p) => p.f.locations.length > 0)) tourEvent('cs-location-set');
    if (lessonStep === 'hm-select' && (allMatches || (results.length > 0 && results.every((r) => selectedIds.has(r.id))))) tourEvent('cs-selected');
  }, [lessonStep, personas, personasEnabled, allMatches, results, selectedIds]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/universe/status', { headers: authHeaders() });
        const d = await resp.json();
        setStatus(resp.ok ? d : { people: 0, companies: 0, error: d.error || 'Universe unreachable' });
      } catch {
        setStatus({ people: 0, companies: 0, error: 'Universe unreachable' });
      }
    })();
  }, [authHeaders]);

  const personaPayload = (f: SearchFilters) => ({
    keywordGroups: keywordGroupsPayload(f),
    titles: f.titles, titleScope: f.titleScope, locations: f.locations,
    headlineKeywords: f.headlineKeywords, summaryKeywords: f.summaryKeywords,
    anywhereKeywords: f.anywhereKeywords, skills: f.skills,
    roleKeywords: f.roleKeywords, roleScope: f.roleScope,
    employmentTypes: expandEmploymentGroups(f.employmentTypes), employmentScope: f.employmentScope,
    industries: f.industries, industryScope: f.industryScope,
    schools: f.schools, degrees: f.degrees,
    tenureMin: f.tenureMin || undefined, tenureMax: f.tenureMax || undefined,
    joinedCompanyMin: f.joinedMin || undefined, joinedCompanyMax: f.joinedMax || undefined,
    experienceMin: f.experienceMin || undefined, experienceMax: f.experienceMax || undefined,
    languages: f.languages, languageLevel: f.languageLevel || undefined, certifications: f.certifications,
    near: nearPayload(f),
    ...notPayload(f),
  });

  // Seeded companies are the fixed frame; with personas enabled they are
  // OR-combined over it server-side, otherwise it's one flat search (no
  // persona tags, no group chips).
  const payload = (ps?: Persona[], seed?: string[], enabledOverride?: boolean) => {
    const st = stateRef.current;
    const list = ps || st.personas;
    const base = { companies: seed || st.seedEntries, companyScope: 'current' };
    const enabled = enabledOverride ?? st.personasEnabled;
    if (!enabled) return { ...base, ...personaPayload((list[0] || { f: EMPTY_FILTERS }).f) };
    const groups = list.filter((p) => personaCount(p.f) > 0);
    return { ...base, orGroups: groups.length ? groups.map((p) => personaPayload(p.f)) : [] };
  };

  // Lesson: the final search, fetched ahead (see LESSON_FINAL).
  const lessonPrefetch = useRef<{ key: string; promise: Promise<{ ok: boolean; d: any }> } | null>(null);

  // A search with no location and no company list reads everyone in the
  // database — ask first (once per visit), never during a lesson.
  const [globalAsk, setGlobalAsk] = useState<null | 'list' | 'grouped'>(null);
  const [locationSignal, setLocationSignal] = useState(0);
  const globalOkRef = useRef(false);
  const askBeforeGlobal = (kind: 'list' | 'grouped') => {
    if (inLesson || globalOkRef.current || seedEntries.length || hasPeopleLocation(payload())) return false;
    setGlobalAsk(kind);
    return true;
  };

  const runSearch = useCallback(async (p: number, ps?: Persona[], seed?: string[], enabledOverride?: boolean) => {
    setSearching(true); setError(null); setPage(p); setGroupRows(null); setView('list');
    // Phones: the results are under the filters — jump down to them.
    try { if (window.matchMedia('(max-width: 767px)').matches) setTimeout(() => document.querySelector('[data-tour="cs-results"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); } catch { /* ignore */ }
    setAllMatches(false);
    const pre = lessonPrefetch.current;
    if (p === 0 && pre && pre.key === JSON.stringify(payload(ps, seed, enabledOverride))) {
      const { ok, d } = await pre.promise;
      if (ok) {
        setResults(d.people || []);
        if (d.total != null) { setTotal(d.total); setGroupCounts(d.groupCounts || null); }
        tourEvent('cs-results');
        setSearching(false);
        return;
      }
    }
    try {
      const resp = await fetch('/api/universe/search', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: payload(ps, seed, enabledOverride), limit: PAGE_SIZE, offset: p * PAGE_SIZE, skipCount: p > 0 }),
      });
      const d = await resp.json();
      if (resp.ok) {
        setResults(d.people || []);
        if (d.total != null) { setTotal(d.total); setGroupCounts(d.groupCounts || null); }
        if (p === 0) tourEvent('cs-results');
      }
      else setError(d.error || 'Search failed');
    } catch { setError('Search failed'); }
    setSearching(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  useEffect(() => {
    if (!inLesson || !seedEntries.length || lessonPrefetch.current) return;
    const body = payload(LESSON_FINAL, seedEntries, true);
    lessonPrefetch.current = {
      key: JSON.stringify(body),
      promise: fetch('/api/universe/search', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: body, limit: PAGE_SIZE, offset: 0, skipCount: false }),
      }).then(async (r) => ({ ok: r.ok, d: await r.json() })).catch(() => ({ ok: false, d: null })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inLesson, seedEntries]);

  const runGrouped = async () => {
    setSearching(true); setError(null); setView('grouped');
    try {
      const resp = await fetch('/api/universe/search/by-company', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: payload() }),
      });
      const d = await resp.json();
      if (resp.ok) { setGroupRows(d.companies || []); setResults([]); setTotal(null); setGroupCounts(null); }
      else setError(d.error || 'Grouped search failed');
    } catch { setError('Grouped search failed'); }
    setSearching(false);
  };

  const toggleSelected = (id: number) => {
    setAllMatches(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addSelectedAsHiringManagers = async () => {
    if (!review || adding || (!allMatches && !selectedIds.size)) return;
    setAdding(true); setAddResult(null);
    try {
      const resp = await fetch('/api/universe/add-to-project', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: review.projectId,
          ...(review.companyId ? { companyId: review.companyId } : {}),
          ...(allMatches ? { all: true, filters: payload() } : { ids: Array.from(selectedIds), filters: payload() }),
          as: 'hiring_managers',
        }),
      });
      const d = await resp.json();
      if (resp.ok) {
        setHmJob({ ...d, inserted: 0, skipped: 0, done: false, error: null }); tourEvent('cs-hm-started');
        if (d.limited) requestTrialUpgrade({ reason: 'import', to: d.limited.to, of: d.limited.of, what: 'contacts' });
      }
      else { setError(d.error || 'Add failed'); setAdding(false); }
    } catch { setError('Add failed'); setAdding(false); }
  };

  // Poll the add job.
  useEffect(() => {
    if (!hmJob?.jobId || hmJob.done) return;
    const t = setInterval(async () => {
      try {
        const resp = await fetch(`/api/universe/promote-status/${hmJob.jobId}`, { headers: authHeaders() });
        if (resp.status === 404) {
          setHmJob((j: any) => (j ? { ...j, done: true, error: 'The add was interrupted (server restarted) — check the project, some may have been added.' } : j));
          return;
        }
        if (resp.ok) { const d = await resp.json(); setHmJob((j: any) => (j ? { ...j, ...d } : j)); }
      } catch { /* transient — keep polling */ }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hmJob?.jobId, hmJob?.done]);

  // Done → say what happened, then open the project on its Hiring Managers list.
  useEffect(() => {
    if (!hmJob?.done || !review) return;
    setAdding(false);
    setSelectedIds(new Set());
    setAllMatches(false);
    if (hmJob.error) return;
    setAddResult(`Added ${Number(hmJob.inserted || 0).toLocaleString()} hiring manager${hmJob.inserted === 1 ? '' : 's'} to ${review.companyName || review.projectName || 'the project'}` +
      (hmJob.skipped ? ` (${Number(hmJob.skipped).toLocaleString()} already in the project)` : ''));
    tourEvent('cs-hm-added');
    const t = setTimeout(() => { releaseLeaveGuard(); window.location.assign(`/project/${review.projectId}?data=managers`); }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hmJob?.done]);

  const startImport = async () => {
    if (!importName.trim() || importJob) return;
    const activePersonas = personasEnabled ? personas.filter((p) => personaCount(p.f) > 0) : [];
    try {
      const resp = await fetch('/api/universe/promote', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: importName.trim(),
          filters: payload(),
          groupNames: activePersonas.map((p) => p.name),
        }),
      });
      const d = await resp.json();
      if (resp.ok) setImportJob({ ...d, inserted: 0, done: false, error: null });
      else setError(d.error || 'Import failed');
    } catch { setError('Import failed'); }
  };

  useEffect(() => {
    if (!importJob?.jobId || importJob.done) return;
    const t = setInterval(async () => {
      try {
        const resp = await fetch(`/api/universe/promote-status/${importJob.jobId}`, { headers: authHeaders() });
        if (resp.status === 404) {
          setImportJob((p: any) => p ? { ...p, done: true, error: 'The import was interrupted (server restarted). Check the project — it may be partial.' } : p);
          return;
        }
        if (!resp.ok) return;
        const d = await resp.json();
        setImportJob((p: any) => (p ? { ...p, ...d } : p));
      } catch { /* transient */ }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importJob?.jobId, importJob?.done]);

  useEffect(() => {
    if (importJob?.done && !importJob.error) {
      const t = setTimeout(() => { releaseLeaveGuard(); window.location.assign(`/project/${importJob.projectId}`); }, 900);
      return () => clearTimeout(t);
    }
  }, [importJob?.done, importJob?.error, importJob?.projectId]);

  // Builder output → a ContactSearch filter set. Company-side fields are
  // DROPPED — the seeded companies are this page's fixed frame.
  // AI / saved output → editable filters, per-field keywords as groups.
  const builtToFilters = (f: any): SearchFilters => withKeywordGroups({
    ...builtToFiltersRaw(f),
    ...(f.near && f.near.place ? { nearPlace: String(f.near.place), nearMiles: String(f.near.miles || 25) } : {}),
    keywordGroups: (Array.isArray(f.keywordGroups) ? f.keywordGroups : []).map((g: any) => ({
      terms: g.terms || [], exclude: g.exclude || [], fields: g.fields || [],
    })),
  });
  const builtToFiltersRaw = (f: any): SearchFilters => ({
    ...EMPTY_FILTERS,
    titles: f.titles || [], locations: f.locations || [],
    anywhereKeywords: f.anywhereKeywords || [], skills: f.skills || [],
    headlineKeywords: f.headlineKeywords || [], summaryKeywords: f.summaryKeywords || [],
    roleKeywords: f.roleKeywords || [], employmentTypes: f.employmentTypes || [],
    industries: f.industries || [], schools: f.schools || [], degrees: f.degrees || [],
    titleScope: f.titleScope || 'current', roleScope: f.roleScope || 'current',
    employmentScope: f.employmentScope || 'current', industryScope: f.industryScope || 'current',
    tenureMin: f.tenureMin != null ? String(f.tenureMin) : '',
    tenureMax: f.tenureMax != null ? String(f.tenureMax) : '',
    joinedMin: f.joinedCompanyMin != null ? String(f.joinedCompanyMin) : '',
    joinedMax: f.joinedCompanyMax != null ? String(f.joinedCompanyMax) : '',
    experienceMin: f.experienceMin != null ? String(f.experienceMin) : '',
    experienceMax: f.experienceMax != null ? String(f.experienceMax) : '',
    titlesExclude: f.titlesExclude || [], skillsExclude: f.skillsExclude || [],
    headlineKeywordsExclude: f.headlineKeywordsExclude || [], employmentSignals: !!f.employmentSignals,
    languages: f.languages || [], languageLevel: f.languageLevel || '', certifications: f.certifications || [],
  });

  const buildSearch = async () => {
    if (!buildPrompt.trim() || building) return;
    setBuilding(true); setBuildNote(null); setError(null);
    try {
      const resp = await fetch('/api/universe/build-search', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: buildPrompt.trim(),
          context: 'The companies are already fixed by a project seed. Build PEOPLE filters only — never companies, companyQuery, headcountMin/Max, foundedAfter/Before.',
        }),
      });
      const d = await resp.json();
      if (!resp.ok) { setError(d.error || 'Could not build the search'); setBuilding(false); return; }
      const ps: { name: string; filters: any }[] = d.personas || [];
      const next: Persona[] = ps.map((p) => ({ name: p.name, f: builtToFilters(p.filters) }));
      setPersonas(next);
      setActive(0);
      setPersonasEnabled(next.length > 1);
      if (d.note) setBuildNote(d.note);
      setBuildGaps(Array.isArray(d.notApplied) ? d.notApplied : []);
      // Fill the filters only — the user reviews them and presses Search.
      setResults([]); setTotal(null); setGroupRows(null);
    } catch { setError('Could not build the search'); }
    setBuilding(false);
  };

  // Find Contacts hand-off from a project's Companies tab.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(FHM_SEED_KEY);
      if (!raw) return;
      sessionStorage.removeItem(FHM_SEED_KEY);
      const seed = JSON.parse(raw);
      if (!Array.isArray(seed.entries) || !seed.entries.length) return;
      const entries = seed.entries.slice(0, 25000);
      setSeedEntries(entries);
      setSeedLabel(seed.label || `${entries.length} companies`);
      if (seed.intent === 'candidates') setIntent('candidates');
      if (seed.review && seed.review.projectId) setReview(seed.review);
      runSearch(0, undefined, entries);
    } catch { /* bad seed — start blank */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPersona = () => {
    if (!personasEnabled) { setPersonasEnabled(true); return; }
    if (personas.length >= MAX_GROUPS) return;
    setPersonas([...personas, { name: `Persona ${personas.length + 1}`, f: EMPTY_FILTERS }]);
    setActive(personas.length);
  };
  // A copy of the persona being edited, with all its filters.
  const duplicatePersona = () => {
    if (personas.length >= MAX_GROUPS) return;
    const src = personas[active];
    setPersonas([...personas, { name: `${src.name} (copy)`, f: JSON.parse(JSON.stringify(src.f)) }]);
    setActive(personas.length);
  };
  const removePersona = (i: number) => {
    if (personas.length === 1) return;
    const next = personas.filter((_, j) => j !== i);
    setPersonas(next);
    setActive(Math.max(0, Math.min(active > i ? active - 1 : active, next.length - 1)));
  };

  const colorOf = (i: number) => GROUP_COLORS[i % GROUP_COLORS.length];

  return (
    <div className="p-5 max-w-[1700px] mx-auto max-md:p-0">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="text-xl font-bold text-gray-900">{intent === 'candidates' ? 'Candidate Search' : 'Hiring Manager Search'}</h1>
        {status && !status.error && (
          <span className="text-xs text-gray-500 tabular-nums">
            {Number(status.people).toLocaleString()} people · {Number(status.companies).toLocaleString()} companies in the universe
          </span>
        )}
        {status?.error && <span className="text-xs text-red-500">{status.error}</span>}
        <a href="/search" className="ml-auto text-xs text-gray-400 hover:text-gray-700">Full search →</a>
      </div>

      {/* Saved templates (+ the "save before you leave?" prompt). The company
          seed isn't part of a template: it's what the template is run on. */}
      <SearchTemplatesBar
        page="contact-search"
        tone="violet"
        hasFilters={personas.some((p) => personaCount(p.f) > 0)}
        getState={() => ({ v: 1, personas, personasEnabled, active, buildPrompt })}
        applyState={(t: any) => {
          const ps: Persona[] = (Array.isArray(t.personas) && t.personas.length ? t.personas : [{ name: 'Persona 1', f: EMPTY_FILTERS }])
            .map((p: any, i: number) => ({ name: p.name || `Persona ${i + 1}`, f: { ...EMPTY_FILTERS, ...(p.f || {}) } }));
          setPersonas(ps);
          setActive(Math.min(Math.max(0, t.active || 0), ps.length - 1));
          setPersonasEnabled(!!t.personasEnabled && ps.length > 1);
          setBuildPrompt(t.buildPrompt || '');
          setResults([]); setTotal(null); setGroupRows(null); setGroupCounts(null);
        }}
      />

      {/* Describe-it search — people filters only; the seed stays the frame. */}
      <div data-tour="cs-describe" className="mb-3 bg-white rounded-xl border border-gray-200 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-base" aria-hidden>✨</span>
          <input
            className="flex-1 border-0 text-sm focus:outline-none placeholder:text-gray-400"
            placeholder={`Describe who you're looking for at these companies — e.g. "engineering leaders with a data science background" or "finance directors, 3+ years in role"`}
            value={buildPrompt}
            onChange={(e) => setBuildPrompt(e.target.value)}
            onPaste={(e) => pasteListIntoPrompt(e, buildPrompt, setBuildPrompt)}
            onKeyDown={(e) => { if (e.key === 'Enter') buildSearch(); }}
            disabled={building}
          />
          <button
            className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 whitespace-nowrap"
            disabled={building || !buildPrompt.trim()}
            onClick={buildSearch}
          >
            {building ? 'Building…' : 'Build search'}
          </button>
        </div>
        {buildNote && (
          <div className="mt-1.5 text-xs text-gray-500 flex items-start gap-2">
            <span className="flex-1">{buildNote} — review the filters on the left, then press Search.</span>
            <button className="text-gray-300 hover:text-gray-600" onClick={() => setBuildNote(null)}>✕</button>
          </div>
        )}
        <BuildGaps items={buildGaps} />
      </div>

      {seedLabel && (
        <div data-tour="cs-seed" className="mb-3 flex items-center gap-2 bg-violet-50 border border-violet-200 text-violet-800 text-sm rounded-lg px-3 py-2">
          <span>
            {review
              ? (review.companyName
                ? <>Reviewing <strong>{review.companyName}</strong> — no hiring managers yet. Tick the people who hire, then add them; refine with the filters if the list is long.</>
                : <>Hiring managers at <strong>{seedLabel}</strong> — tick the people who hire and add them to <strong>{review.projectName || 'your project'}</strong>; refine with the filters if the list is long.</>)
              : <>{intent === 'candidates' ? 'Candidates' : 'Hiring managers'} at <strong>{seedLabel}</strong> — {personasEnabled
                ? <>results combine anyone matching <em>any</em> persona at these companies.</>
                : 'refine with the filters, then search everyone with a current role there.'}</>}
          </span>
          {intent === 'hiring-managers' && (
            <button
              className="ml-auto text-xs underline hover:no-underline whitespace-nowrap"
              onClick={() => setF({ titles: Array.from(new Set([...cur.f.titles, ...HIRING_MANAGER_TITLES])), titleScope: 'current' })}
            >
              + hiring manager titles
            </button>
          )}
          <button className={`${intent === 'candidates' ? 'ml-auto ' : ''}text-violet-400 hover:text-violet-900`} title="Clear the company seed"
            onClick={() => { setSeedEntries([]); setSeedLabel(null); setResults([]); setTotal(null); setGroupRows(null); setGroupCounts(null); }}>×</button>
        </div>
      )}

      {/* Phones: filters on top, results underneath */}
      <div className="flex gap-4 items-start max-md:flex-col max-md:items-stretch">
        {/* Persona rail */}
        <div data-tour="cs-filters" className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col max-h-[calc(100vh-150px)] max-md:w-full max-md:max-h-none">
          {/* Persona tabs — opt-in; each is its own complete search, OR-combined */}
          {personasEnabled && (
          <div data-tour="cs-persona-tabs" className="px-3 pt-2.5 pb-2 border-b border-gray-100">
            <div className="flex flex-wrap gap-1.5">
              {personas.map((p, i) => {
                const c = colorOf(i);
                const n = personaCount(p.f);
                return (
                  <button
                    key={i}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${i === active ? `${c.bg} ${c.text} border-transparent` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}
                    onClick={() => setActive(i)}
                    title={`Edit ${p.name}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${c.solid}`} />
                    {p.name}
                    {n > 0 && <span className="tabular-nums opacity-70">({n})</span>}
                    {personas.length > 1 && (
                      <span
                        role="button"
                        className="opacity-50 hover:opacity-100"
                        title={`Remove ${p.name}`}
                        onClick={(e) => { e.stopPropagation(); removePersona(i); }}
                      >×</span>
                    )}
                  </button>
                );
              })}
              {personas.length < MAX_GROUPS && (
                <button className="px-2.5 py-1 rounded-full text-xs border border-dashed border-gray-300 text-gray-400 hover:text-gray-700 hover:border-gray-500"
                  onClick={addPersona} title="Add another persona — results combine anyone matching any persona">
                  + persona
                </button>
              )}
              {personas.length < MAX_GROUPS && (
                <button className="px-2.5 py-1 rounded-full text-xs border border-dashed border-gray-300 text-gray-400 hover:text-gray-700 hover:border-gray-500"
                  onClick={duplicatePersona} title={`Copy ${cur.name} with all its filters`}>
                  ⧉ duplicate
                </button>
              )}
            </div>
            <input
              className="mt-2 w-full border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400"
              value={cur.name}
              onChange={(e) => setPersonas(personas.map((p, i) => (i === active ? { ...p, name: e.target.value } : p)))}
              placeholder="Persona name (e.g. Engineering Directors)"
            />
          </div>
          )}

          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-500">{personasEnabled ? `Filters for ${cur.name}` : 'Filters'}</span>
            <span className="flex items-center gap-2">
              {!personasEnabled && (
                <button
                  data-tour="cs-personas-btn"
                  className="text-xs text-violet-600 hover:text-violet-800"
                  title="Run several searches at once (e.g. Engineering Directors + data science OR any Talent Acquisition) and combine the results — persona names become tags on import"
                  onClick={addPersona}
                >
                  + Personas
                </button>
              )}
              {personaCount(cur.f) > 0 && (
                <button className="text-xs text-gray-400 hover:text-gray-700" onClick={() => setF({ ...EMPTY_FILTERS })}>
                  Clear
                </button>
              )}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            <FilterSection title="Job titles" count={cur.f.titles.length + cur.f.titlesExclude.length} defaultOpen tour="cs-filter-titles">
              <div className="flex justify-end">
                <ScopeSelect value={cur.f.titleScope} allowPast allowPrimary onChange={(v) => setF({ titleScope: v })} />
              </div>
              <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <TitleSyntaxTip />
                Plain words · "exact phrase" · =exact title — click ⓘ for examples
              </div>
              <ChipInput label="" values={cur.f.titles} onChange={(v) => setF({ titles: v })} placeholder={'e.g. head of talent · "talent acquisition" · =CTO'}
                excluded={cur.f.titlesExclude} onExcludedChange={(ex, inc) => setF({ titlesExclude: ex, titles: inc })} />
              <SuggestRelatedTitles titles={cur.f.titles} authHeaders={authHeaders} accent="violet"
                context={[...cur.f.keywordGroups.flatMap((g) => g.terms), ...cur.f.locations].join(', ')}
                onAdd={(v) => setF({ titles: v })} />
            </FilterSection>
            <FilterSection title="Location" count={cur.f.locations.length + (cur.f.nearPlace.trim() ? 1 : 0)} tour="cs-filter-location" openSignal={locationSignal}>
              <LocationInput values={cur.f.locations} authHeaders={authHeaders} tone="violet" onChange={(v) => setF({ locations: v })} placeholder="Start typing a place — e.g. London" />
              <NearInput place={cur.f.nearPlace} miles={cur.f.nearMiles} authHeaders={authHeaders} tone="violet" onChange={(place, miles) => setF({ nearPlace: place, nearMiles: miles })} />
            </FilterSection>
            <FilterSection title="Keywords" count={cur.f.keywordGroups.reduce((n, g) => n + g.terms.length + g.exclude.length, 0)}>
              <KeywordGroupsEditor key={active} groups={cur.f.keywordGroups} authHeaders={authHeaders} context={cur.f.titles.join(', ')} accent="violet"
                onChange={(g) => setF({ keywordGroups: g })} />
            </FilterSection>
            <FilterSection title="Skills" count={cur.f.skills.length}>
              <ChipInput label="" values={cur.f.skills} splitCommas onChange={(v) => setF({ skills: v })} placeholder="e.g. Kubernetes, Terraform (comma = OR)"
                excluded={cur.f.skillsExclude} onExcludedChange={(ex, inc) => setF({ skillsExclude: ex, skills: inc })} />
            </FilterSection>
            <FilterSection title="Employment type" count={cur.f.employmentTypes.length}>
              <div className="flex justify-end">
                <ScopeSelect value={cur.f.employmentScope} onChange={(v) => setF({ employmentScope: v })} />
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                {EMPLOYMENT_TYPE_GROUPS.map(({ label: t }) => (
                  <label key={t} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={cur.f.employmentTypes.indexOf(t) !== -1}
                      onChange={(e) => setF({
                        employmentTypes: e.target.checked
                          ? cur.f.employmentTypes.concat([t])
                          : cur.f.employmentTypes.filter((x) => x !== t),
                      })}
                    />
                    {t}
                  </label>
                ))}
              </div>
              {signalsApply(cur.f.employmentTypes) && (
                <EmploymentSignalsToggle checked={cur.f.employmentSignals} onChange={(v) => setF({ employmentSignals: v })} />
              )}
            </FilterSection>
            <FilterSection title="Industry" count={cur.f.industries.length}>
              <div className="flex justify-end">
                <ScopeSelect value={cur.f.industryScope} allowPast onChange={(v) => setF({ industryScope: v })} />
              </div>
              <ChipInput label="" values={cur.f.industries} onChange={(v) => setF({ industries: v })} placeholder="e.g. Telecommunications"
                suggestions={industryOpts?.role.values} suggestionLabels={industryOpts?.role.labels} suggestionsId="contact-industries" />
            </FilterSection>
            <FilterSection title="Education" count={cur.f.schools.length + cur.f.degrees.length}>
              <SuggestChipInput kind="school" label="School" tone="violet" values={cur.f.schools} authHeaders={authHeaders} onChange={(v) => setF({ schools: v })} placeholder="Start typing — e.g. Edinburgh Napier" />
              <SuggestChipInput kind="degree" label="Degree or field" tone="violet" values={cur.f.degrees} authHeaders={authHeaders} onChange={(v) => setF({ degrees: v })} placeholder="Start typing — e.g. Computer Science" />
            </FilterSection>
            <LanguageCertSections f={cur.f} set={setF} languageNames={languageNames} />
            <FilterSection title="Tenure & experience" count={(cur.f.tenureMin ? 1 : 0) + (cur.f.tenureMax ? 1 : 0) + (cur.f.joinedMin ? 1 : 0) + (cur.f.joinedMax ? 1 : 0) + (cur.f.experienceMin ? 1 : 0) + (cur.f.experienceMax ? 1 : 0)}>
              <RangeInput label="Years in current role" min={cur.f.tenureMin} max={cur.f.tenureMax}
                onMin={(v) => setF({ tenureMin: v })} onMax={(v) => setF({ tenureMax: v })} />
              <RangeInput label="Years in current company" min={cur.f.joinedMin} max={cur.f.joinedMax}
                onMin={(v) => setF({ joinedMin: v })} onMax={(v) => setF({ joinedMax: v })} />
              <RangeInput label="Total experience (years)" min={cur.f.experienceMin} max={cur.f.experienceMax}
                onMin={(v) => setF({ experienceMin: v })} onMax={(v) => setF({ experienceMax: v })} />
            </FilterSection>
          </div>
          {globalAsk && (
            <div className="px-3 pt-3">
              <GlobalSearchNotice tone="violet"
                onAddLocation={() => { setGlobalAsk(null); setLocationSignal((n) => n + 1); }}
                onSearchAnyway={() => {
                  globalOkRef.current = true;
                  const go = globalAsk;
                  setGlobalAsk(null);
                  if (go === 'grouped') runGrouped(); else runSearch(0);
                }}
              />
            </div>
          )}
          <div className="p-3 border-t border-gray-100 flex gap-2">
            <button
              data-tour="cs-run"
              className="flex-1 px-3 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
              disabled={searching}
              onClick={() => { if (!askBeforeGlobal('list')) runSearch(0); }}
            >
              {searching && view === 'list' ? 'Searching…' : `Search${personasEnabled && personas.filter((p) => personaCount(p.f) > 0).length > 1 ? ' all personas' : ''}`}
            </button>
            <button
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              disabled={searching}
              title="Group matches by their current company"
              onClick={() => { if (!askBeforeGlobal('grouped')) runGrouped(); }}
            >
              By company
            </button>
          </div>
        </div>

        {/* Results */}
        <div data-tour="cs-results" className="flex-1 min-w-0 max-md:w-full max-md:scroll-mt-16">
          {error && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          {loaderPhase === 'on' || loaderPhase === 'leaving' ? (
            <SearchLoader leaving={loaderPhase === 'leaving'} />
          ) : view === 'grouped' && groupRows !== null ? (
            <>
              <div className="text-sm text-gray-700 font-semibold tabular-nums mb-3">
                {groupRows.length.toLocaleString()} compan{groupRows.length === 1 ? 'y' : 'ies'}
                <span className="text-gray-400 font-normal"> · matched people grouped by current employer</span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-auto" style={{ maxHeight: 'calc(100vh - 240px)' }}>
                <table className="w-full text-[13px]">
                  <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                    <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                      <th className="px-3 py-2.5 min-w-[240px]">Company</th>
                      <th className="px-3 py-2.5 min-w-[150px]">Domain</th>
                      <th className="px-3 py-2.5 min-w-[110px] text-right">Matched</th>
                      <th className="px-3 py-2.5 min-w-[150px] text-right">Employees in universe</th>
                      <th className="px-3 py-2.5 min-w-[70px]">LinkedIn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRows.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No companies matched</td></tr>
                    )}
                    {groupRows.map((g: any) => (
                      <tr key={g.key} className={`border-t border-gray-100 hover:bg-violet-50/60 ${g.requested ? 'bg-amber-50/40' : ''}`}>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {g.name}
                          {g.requested && <span className="ml-2 text-[9px] uppercase tracking-wide bg-amber-100 text-amber-700 rounded px-1 py-0.5">your list</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{g.domain || ''}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-violet-700">{g.matched}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">{g.employees}</td>
                        <td className="px-3 py-2">
                          {g.linkedin_url && (
                            <a href={g.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-800 text-xs">Open ↗</a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : total === null ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
              {seedEntries.length
                ? 'Build personas on the left, then hit Search.'
                : <>This page is seeded from a project: open a project → Companies tab → select companies → <strong>Find Contacts</strong>. For open-ended searching use the <a className="text-violet-600 underline" href="/search">full search</a>.</>}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="text-sm text-gray-700 font-semibold tabular-nums">
                  {total.toLocaleString()} match{total === 1 ? '' : 'es'}
                </div>
                {groupCounts && groupCounts.length > 1 && groupCounts.map((n, i) => {
                  const c = colorOf(i);
                  return (
                    <span key={i} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                      <span className={`w-2 h-2 rounded-full ${c.solid}`} />
                      {personas.filter((p) => personaCount(p.f) > 0)[i]?.name || `Persona ${i + 1}`}: {n.toLocaleString()}
                    </span>
                  );
                })}
                {review ? (
                  <div className="ml-auto flex items-center gap-2">
                    {addResult && <span className="text-xs text-emerald-600">{addResult}</span>}
                    {addResult && review.projectId && (
                      <a data-tour="cs-back-to-project" href={`/project/${review.projectId}`}
                        className="text-sm font-medium text-violet-700 hover:text-violet-900 whitespace-nowrap">
                        Back to project →
                      </a>
                    )}
                    <button
                      data-tour="cs-add-hm"
                      className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                      disabled={(!allMatches && !selectedIds.size) || adding}
                      onClick={addSelectedAsHiringManagers}
                    >
                      {adding ? 'Adding…'
                        : allMatches ? `Add all ${(total || 0).toLocaleString()} matches as hiring managers →`
                        : `Add ${selectedIds.size || ''} selected as hiring managers →`}
                    </button>
                  </div>
                ) : total > 0 && (
                  <div className="ml-auto flex items-center gap-2 flex-wrap">
                    {importJob ? (
                      importJob.error
                        ? <span className="text-xs text-red-600">{importJob.error}</span>
                        : <span className="text-xs text-gray-600 tabular-nums">
                            {importJob.done ? 'Done — opening project…'
                              : importJob.phase === 'companies'
                                ? `Adding companies ${importJob.companiesDone || 0} / ${importJob.companiesTotal ?? '…'}`
                                : `Adding candidates ${(importJob.inserted || 0).toLocaleString()} / ${(importJob.total || 0).toLocaleString()}`}
                          </span>
                    ) : (
                      <>
                        <input
                          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-violet-500"
                          placeholder="New project name"
                          value={importName}
                          onChange={(e) => setImportName(e.target.value)}
                        />
                        <button
                          className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                          disabled={!importName.trim() || (!!status?.importLimit && total > status.importLimit)}
                          title={status?.importLimit && total > status.importLimit
                            ? `Your ${status.importLimitPlan} plan imports up to ${status.importLimit.toLocaleString()} people per project — narrow the search to import`
                            : 'Create a project from every match — persona names become candidate tags'}
                          onClick={startImport}
                        >
                          Add {total.toLocaleString()} to project →
                        </button>
                        {!!status?.importLimit && total > status.importLimit && (
                          <span className="text-[11px] text-amber-700">
                            Over your {status.importLimitPlan} plan's {status.importLimit.toLocaleString()}-per-project limit
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              {review && (total || 0) > results.length && results.length > 0 && results.every((r) => selectedIds.has(r.id)) && (
                <div className="mb-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-900 flex items-center gap-3">
                  {allMatches ? (
                    <>
                      <span>All <b>{(total || 0).toLocaleString()}</b> matches selected.</span>
                      <button className="underline" onClick={() => setAllMatches(false)}>Only this page</button>
                    </>
                  ) : (
                    <>
                      <span>All {results.length} on this page selected.</span>
                      <button className="underline font-medium" onClick={() => setAllMatches(true)}>
                        Select all {(total || 0).toLocaleString()} matches
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className={`bg-white rounded-xl border border-gray-200 overflow-auto ${searching ? 'opacity-50 pointer-events-none' : ''}`} style={{ maxHeight: 'calc(100vh - 250px)' }}>
                <table className="text-[13px]" style={{ minWidth: '1400px', width: '100%' }}>
                  <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                    <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                      {review && (
                        <th data-tour="cs-select" className="px-2 py-2.5 w-8">
                          <input
                            type="checkbox"
                            title="Select everyone on this page"
                            checked={results.length > 0 && results.every((r) => selectedIds.has(r.id))}
                            onChange={(e) => {
                              const next = new Set(selectedIds);
                              results.forEach((r) => { if (e.target.checked) next.add(r.id); else next.delete(r.id); });
                              setSelectedIds(next);
                              if (!e.target.checked) setAllMatches(false);
                            }}
                          />
                        </th>
                      )}
                      <th className="px-3 py-2.5 sticky left-0 bg-gray-50 z-20 min-w-[170px]">Name</th>
                      <th className="px-3 py-2.5 min-w-[200px]">Title</th>
                      <th className="px-3 py-2.5 min-w-[170px]">Company</th>
                      <th className="px-3 py-2.5 min-w-[180px]">Location</th>
                      <th className="px-3 py-2.5 min-w-[80px] text-right">In role</th>
                      <th className="px-3 py-2.5 min-w-[70px] text-right">Exp</th>
                      <th className="px-3 py-2.5 min-w-[260px]">Headline</th>
                      <th className="px-3 py-2.5 min-w-[260px]">Skills</th>
                      <th className="px-3 py-2.5 min-w-[70px]">LinkedIn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.length === 0 && (
                      <tr><td colSpan={review ? 10 : 9} className="px-4 py-8 text-center text-gray-400">No matches</td></tr>
                    )}
                    {results.map((p) => (
                      <tr key={p.id} className={`border-t border-gray-100 hover:bg-violet-50/60 ${selectedIds.has(p.id) ? 'bg-violet-50/70' : ''}`}>
                        {review && (
                          <td className="px-2 py-2 w-8">
                            <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelected(p.id)} />
                          </td>
                        )}
                        <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white cursor-pointer hover:text-violet-700"
                          onClick={() => openProfile(p.id)} title="View full profile">
                          {p.name}
                          {p.matched_groups && groupCounts && groupCounts.length > 1 && (
                            <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
                              {p.matched_groups.map((gi) => {
                                const activePersonas = personas.filter((x) => personaCount(x.f) > 0);
                                return (
                                  <span key={gi} className={`w-2 h-2 rounded-full ${colorOf(gi).solid}`}
                                    title={activePersonas[gi]?.name || `Persona ${gi + 1}`} />
                                );
                              })}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          <span className="block">{p.title || ''}</span>
                          {(p as any).matched_current_role && (
                            <span className="block text-[11px] text-violet-700" title="This person also holds this current role — it's what matched your title filter">
                              ↳ {(p as any).matched_current_role} · concurrent role
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{p.company || ''}</td>
                        <td className="px-3 py-2 text-gray-500">{p.location || ''}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                          {p.current_start ? `${Math.max(0, Math.round((Date.now() - new Date(p.current_start).getTime()) / 31557600000 * 10) / 10)}y` : ''}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">{p.experience_years != null ? `${p.experience_years}y` : ''}</td>
                        <td className="px-3 py-2 text-gray-500 max-w-[320px] truncate" title={p.headline || ''}>{p.headline || ''}</td>
                        <td className="px-3 py-2 text-gray-500 max-w-[320px] truncate" title={p.skills || ''}>{p.skills || ''}</td>
                        <td className="px-3 py-2">
                          {p.linkedin_url && (
                            <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-800 text-xs">Open ↗</a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > PAGE_SIZE && (
                <div className="flex items-center gap-2 mt-3 text-sm">
                  <button className="px-2.5 py-1 border border-gray-300 rounded-lg disabled:opacity-40" disabled={page === 0 || searching} onClick={() => runSearch(page - 1)}>←</button>
                  <span className="text-gray-500 tabular-nums">Page {page + 1} of {Math.ceil(Math.min(total, 1000) / PAGE_SIZE).toLocaleString()}</span>
                  <button className="px-2.5 py-1 border border-gray-300 rounded-lg disabled:opacity-40" disabled={(page + 1) * PAGE_SIZE >= Math.min(total, 1000) || searching} onClick={() => runSearch(page + 1)}>→</button>
                  {total > 1000 && (
                    <span className="text-[11px] text-gray-400" title="Totals always show, but browsing stops at the first 1,000 — narrow the search, or add matches to the project to work with all of them">
                      Browsing the first 1,000 of {total.toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Profile drawer — the full universe record for a clicked person. */}
      {profile && (() => {
        const sd: any = (profile.raw && (profile.raw.serialized_data || profile.raw)) || {};
        const ym = (x: any) => (x ? String(x).slice(0, 7) : null);
        const range = (a: any, b: any) => {
          const st = ym(a), en = ym(b);
          return st || en ? `${st || '?'} – ${en || 'Present'}` : null;
        };
        const roles = Array.isArray(sd.roles) ? sd.roles : [];
        const edus = Array.isArray(sd.educations) ? sd.educations : [];
        const skills = Array.isArray(sd.skills) ? sd.skills : [];
        return (
          <div className="fixed inset-y-0 right-0 w-[440px] bg-white border-l border-gray-200 shadow-2xl z-40 overflow-y-auto">
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                {sd.photo && !new RegExp('fora' + 'ger', 'i').test(String(sd.photo)) && (
                  <img src={sd.photo} alt="" className="w-12 h-12 object-cover rounded-full flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-semibold text-gray-900">{profile.name || 'Record'}</div>
                  {profile.headline && <div className="text-sm text-gray-500 mt-0.5">{profile.headline}</div>}
                </div>
                <button className="text-gray-400 hover:text-gray-700" onClick={() => setProfile(null)}>✕</button>
              </div>
              {profileLoading ? (
                <div className="text-gray-400 text-sm mt-6">Loading…</div>
              ) : (
                <>
                  <div className="mt-4 space-y-1.5 text-sm">
                    {([['Title', profile.title], ['Company', profile.company], ['Location', profile.location],
                       ['Industry', sd.industry],
                       ['Connections', sd.number_of_connections != null ? `${Number(sd.number_of_connections).toLocaleString()} (${Number(sd.number_of_followers || 0).toLocaleString()} followers)` : null],
                       ['Updated', sd.date_updated ? String(sd.date_updated).slice(0, 10) : null]] as [string, any][])
                      .map(([label, value]) => value ? (
                        <div key={label} className="flex">
                          <span className="w-24 text-gray-400 flex-shrink-0">{label}</span>
                          <span className="text-gray-800 break-words">{value}</span>
                        </div>
                      ) : null)}
                    {profile.linkedin_url && (
                      <div className="flex">
                        <span className="w-24 text-gray-400 flex-shrink-0">LinkedIn</span>
                        <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline break-all">
                          {String(profile.linkedin_url).replace(/^https?:\/\/(www\.)?/, '')}
                        </a>
                      </div>
                    )}
                  </div>
                  {profile.summary && (
                    <div className="mt-5">
                      <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Summary</h3>
                      <p className="text-[13px] text-gray-700 whitespace-pre-wrap">{String(profile.summary).slice(0, 1500)}</p>
                    </div>
                  )}
                  {roles.length > 0 && (
                    <div className="mt-5">
                      <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Career history ({roles.length})</h3>
                      <div className="space-y-2.5">
                        {roles.map((r: any, i: number) => (
                          <div key={i} className="flex gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${!r.end_date ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                            <div className="min-w-0">
                              <div className="text-[13px]">
                                <span className="font-medium text-gray-900">{r.role_title || '—'}</span>
                                {r.organization_name && <span className="text-gray-500"> · {r.organization_name}</span>}
                              </div>
                              <div className="text-[11px] text-gray-400">
                                {range(r.start_date, r.end_date)}
                                {r.location ? ` · ${r.location}` : ''}
                                {r.organization_industry ? ` · ${r.organization_industry}` : ''}
                              </div>
                              {r.description && <p className="text-[11px] text-gray-500 mt-0.5">{String(r.description).slice(0, 220)}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {edus.length > 0 && (
                    <div className="mt-5">
                      <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Education</h3>
                      <div className="space-y-1.5">
                        {edus.map((e: any, i: number) => (
                          <div key={i} className="text-[13px]">
                            <span className="font-medium text-gray-800">{e.school_name || '—'}</span>
                            <span className="text-gray-500">{e.degree ? ` · ${e.degree}` : ''}{e.field_of_study ? `, ${e.field_of_study}` : ''}</span>
                            {range(e.start_date, e.end_date) && <span className="text-[11px] text-gray-400"> · {range(e.start_date, e.end_date)}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {skills.length > 0 && (
                    <div className="mt-5">
                      <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Skills</h3>
                      <div className="flex flex-wrap gap-1">
                        {skills.slice(0, 30).map((sk: any, i: number) => (
                          <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px]">{String(sk)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}
      {/* Adding hiring managers — progress, then the project opens. */}
      {hmJob && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
            <p className="text-sm font-semibold text-gray-900">
              {hmJob.error ? 'Problem adding hiring managers'
                : hmJob.done ? 'Done — opening your project…'
                : `Adding hiring managers to ${review?.companyName || review?.projectName || 'your project'}`}
            </p>
            {hmJob.error ? (
              <>
                <p className="text-sm text-red-600 mt-3">{hmJob.error}</p>
                <div className="flex justify-end gap-2 mt-4">
                  {review?.projectId && (
                    <a href={`/project/${review.projectId}?data=managers`} onClick={() => releaseLeaveGuard()} className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700">Open project</a>
                  )}
                  <button className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200" onClick={() => setHmJob(null)}>Close</button>
                </div>
              </>
            ) : (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1 tabular-nums">
                  <span>Hiring managers</span>
                  <span>{(Number(hmJob.inserted || 0) + Number(hmJob.skipped || 0)).toLocaleString()} / {Number(hmJob.total || 0).toLocaleString()}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full transition-all duration-500"
                    style={{ width: `${hmJob.total ? Math.min(100, Math.round(100 * (Number(hmJob.inserted || 0) + Number(hmJob.skipped || 0)) / hmJob.total)) : 0}%` }} />
                </div>
                <p className="text-[11px] text-gray-400 mt-3">
                  Each one is linked to their company in the project. Keep this tab open until it finishes.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSearch;
